// src/components/admin/AdminUsersSection.jsx
import React from "react";
import { Save, XCircle, Edit, Trash2 } from "lucide-react";

const AdminUsersSection = ({
  users,
  usersLoading,
  editingId,
  editForm,
  setEditForm,
  startEdit,
  cancelEdit,
  saveEdit,
  handleDelete,
  roleOptions,
}) => {
  const editingUser = editingId
    ? users.find((u) => u.id === editingId) || null
    : null;

  return (
    <>
      <div
        className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-200/80 overflow-hidden"
        dir="rtl"
      >
        <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              لیست کاربران سیستم
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-500">
              مدیریت و ویرایش کاربران، نقش‌ها و وضعیت فعال بودن
            </p>
          </div>
        </div>

        {usersLoading ? (
          <div className="py-12 flex justify-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-[3px] border-sky-500 border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-50/80 border-b border-slate-200">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-right font-semibold text-slate-700">
                    نام
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right font-semibold text-slate-700">
                    ایمیل
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right font-semibold text-slate-700">
                    موبایل
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right font-semibold text-slate-700">
                    نقش
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right font-semibold text-slate-700">
                    وضعیت
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right font-semibold text-slate-700">
                    عملیات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-slate-50/70 transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3 align-middle text-xs sm:text-sm text-slate-900">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="px-4 sm:px-6 py-3 align-middle text-xs sm:text-sm text-slate-600">
                      {user.email}
                    </td>
                    <td className="px-4 sm:px-6 py-3 align-middle text-xs sm:text-sm text-slate-900">
                      {user.phone_number || "-"}
                    </td>
                    <td className="px-4 sm:px-6 py-3 align-middle">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium ${
                          user.role === "admin"
                            ? "bg-violet-50 text-violet-800 border border-violet-100"
                            : user.role === "editor"
                            ? "bg-sky-50 text-sky-800 border border-sky-100"
                            : user.role === "accountent"
                            ? "bg-emerald-50 text-emerald-800 border border-emerald-100"
                            : user.role === "viewer"
                            ? "bg-amber-50 text-amber-800 border border-amber-100"
                            : user.role === "client"
                            ? "bg-indigo-50 text-indigo-800 border border-indigo-100"
                            : "bg-slate-50 text-slate-800 border border-slate-100"
                        }`}
                      >
                        {roleOptions.find((r) => r.value === user.role)
                          ?.label || "نامشخص"}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 align-middle text-center">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium ${
                          user.is_active
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : "bg-rose-50 text-rose-700 border border-rose-100"
                        }`}
                      >
                        {user.is_active ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 align-middle">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => startEdit(user)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sky-600 hover:bg-sky-50 transition-colors"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && !usersLoading && (
              <div className="text-center py-10 text-xs sm:text-sm text-slate-500">
                هیچ کاربری یافت نشد.
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODAL: ویرایش کاربر */}
      {editingUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
          dir="rtl"
        >
          <div className="w-full max-w-md sm:max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200/80 px-4 sm:px-6 py-5 sm:py-6 relative">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-col items-end">
                <h3 className="text-base sm:text-lg font-bold text-slate-900">
                  ویرایش کاربر
                </h3>
                <p className="text-[11px] sm:text-xs text-slate-500">
                  {editingUser.username || editingUser.email}
                </p>
              </div>
              <button
                onClick={cancelEdit}
                className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500"
              >
                <XCircle size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 text-xs sm:text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-700 mb-1">
                    نام
                  </label>
                  <input
                    type="text"
                    value={editForm.first_name || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, first_name: e.target.value })
                    }
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-700 mb-1">
                    نام خانوادگی
                  </label>
                  <input
                    type="text"
                    value={editForm.last_name || ""}
                    onChange={(e) =>
                      setEditForm({ ...editForm, last_name: e.target.value })
                    }
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-700 mb-1">
                  ایمیل
                </label>
                <input
                  type="email"
                  value={editForm.email || ""}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-700 mb-1">
                  شماره موبایل
                </label>
                <input
                  type="text"
                  value={editForm.phone_number || ""}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      phone_number: e.target.value,
                    })
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-700 mb-1">
                    نقش
                  </label>
                  <select
                    value={editForm.role || "editor"}
                    onChange={(e) =>
                      setEditForm({ ...editForm, role: e.target.value })
                    }
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                  >
                    {roleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between sm:justify-start sm:flex-row-reverse gap-2 mt-1">
                  <label className="flex items-center gap-2 text-[11px] sm:text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!editForm.is_active}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          is_active: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                    />
                    کاربر فعال باشد
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-5 flex flex-wrap gap-2 sm:gap-3 justify-end">
              <button
                onClick={cancelEdit}
                className="px-3 sm:px-4 py-2 rounded-xl text-[11px] sm:text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors inline-flex items-center gap-1.5"
              >
                <XCircle size={16} />
                انصراف
              </button>
              <button
                onClick={saveEdit}
                className="px-3 sm:px-4 py-2 rounded-xl text-[11px] sm:text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-md inline-flex items-center gap-1.5 transition-colors"
              >
                <Save size={16} />
                ذخیره تغییرات
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminUsersSection;
